<template>
  <div class="serviced-mortgage-group">
  <div
    class="mortgage-result-servised serviced-mortgage-clickable"
    :class="{'serviced-mortgage-clickable--disabled': !servicedDebt}"
    :role="servicedDebt ? 'button' : null"
    :tabindex="servicedDebt ? 0 : -1"
    :aria-label="servicedDebt ? `Otvoriť detail úveru klienta ${mortgage.applicant?.first_name || ''} ${mortgage.applicant?.last_name || ''}`.trim() : null"
    @click="openDebtDetail"
    @keydown.enter.prevent="openDebtDetail"
    @keydown.space.prevent="openDebtDetail"
  >
    <div class="mortgage-parameters-servised">
      <!-- left part -->
      <div class="servisedmortgageLeft">
        <div @click.stop>
          <user-manager-handler :rules="['priority']" :client-info="mortgage.applicant" client clients/>
        </div>
        <div
          class="wrap150px" style="text-align: start;"
        >
          {{ transformDate(mortgage.nacerpana) }}
        </div>
      </div>
      <!-- right part - mortgagage stats -->
      <div class="servisedmortgageRight">
        <!-- bank logo -->
        <img
          style="object-fit: contain"
          :src="getBankLogoImage(servicedApplication.bank)"
          loading="lazy"
          :alt="servicedApplication.bank"
          class="bank-logo"
        />
        <!-- pozicana suma -->
        <div class="bold wrap150px" v-if="servicedApplication.vytvoreny_debt">
          {{ servicedApplication.vytvoreny_debt.zostatok | dotTheAmount }} EUR
          <br>
          <span class="opacity-30 light">{{ totalAmounts(servicedApplication) | dotTheAmount }} EUR</span>
        </div>
        <!-- sadzba -->
        <div
          class="bold wrap50px"
        >
          {{ servicedApplication.sadzba_schvalena ?? servicedApplication.sadzba_pozadovana }}<span class="upper-index">%</span>
        </div>
        <!-- mesacna splatka -->
        <div
          class="bold wrap100px d-flex align-items-center"
          style="justify-content: center;"
        >
          {{ servicedApplication.splatka | dotTheAmount }} <span class="upper-index">EUR</span>
        </div>
        <!-- fixacia -->
        <div
          class="bold wrap50px"
        >
          FIX {{ calculateFixEndDate() }}
          <span class="upper-index">
            <!-- dorobit vypocet zaciatok splacania + fixacia -->
          </span>
        </div>
        <!-- splatnost hypoteky -->
        <div
          class="bold wrap50px"
        >
          {{ remainingMaturityMonths }}<span class="upper-index">MES.</span>
        </div>
        <div class="d-flex" @click.stop>
          <application-refixation-handler :mortgage="mortgage"/>
          <div class="text-cta" style="margin-left: 20px;">
            <div @click.stop="refinanceMortgage">
              REFINANCOVANIE
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <service-refinancing-check v-if="servicedDebt" :mortgage="mortgage" :debt-id="servicedDebt.id" :active="servicedDebt.is_active !== false"/>
  </div>
</template>

<script>
import translateFromDBValue from "@/mixins/translateFromDBValue";
import dateConverter from "@/mixins/dateConverter";
import UserManagerHandler from "@/components/shared/PopupHandler/Popups/UserManager/UserManagerHandler.vue";
import ApplicationRefixationHandler
  from "@/components/shared/PopupHandler/Popups/ApplicationRefixation/ApplicationRefixationHandler.vue";
import {patchMortgageDebtPlan} from "@/services/brokerService";

import ServiceRefinancingCheck from "./ServiceRefinancingCheck.vue";

export default {
  name: "ServicedMortgage",
  components: {ApplicationRefixationHandler, UserManagerHandler, ServiceRefinancingCheck},
  mixins: [translateFromDBValue, dateConverter],
  props: {
    mortgage: Object,
  },
  created() {
    this._onEvent = (payload) => {
      this.refinanceMortgageForDebt(payload.id)
    }

    this.$root.$on("debt-picked", this._onEvent)
  },
  beforeDestroy() {
    if (this._onEvent) {
      this.$root.$off("debt-picked", this._onEvent)
    }
  },
  computed: {
    servicedApplication() {
      const drawnApplications = this.mortgage.applications.filter((application) => application.stage === "nacerpana");
      return drawnApplications.find((application) => (
        typeof application.vytvoreny_debt === "object"
        && application.vytvoreny_debt?.nahradeny_dlhom == null
      )) || drawnApplications[0];
    },
    servicedDebt() {
      const debt = this.servicedApplication?.vytvoreny_debt;
      return debt && typeof debt === "object" ? debt : null;
    },
    remainingMaturityMonths() {
      return this.servicedApplication?.vytvoreny_debt?.zostavajuca_splatnost_mesiace
        ?? this.servicedApplication?.zostavajuca_splatnost_mesiace
        ?? this.servicedApplication?.splatnost_mesiace
        ?? (this.servicedApplication?.splatnost ? this.servicedApplication.splatnost * 12 : 0);
    }
  },
  data() {
    return {
      slovakMonthAbbr: [
        'Jan', 'Feb', 'Mar', 'Apr', 'Máj', 'Jún',
        'Júl', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'
      ]
    }
  },
  methods: {
    openDebtDetail() {
      const debt = this.servicedDebt;
      const applicant = this.mortgage.applicant;
      if (!debt || !applicant) return;

      const mortgage = JSON.parse(JSON.stringify(this.mortgage));
      const applicantCopy = JSON.parse(JSON.stringify(applicant));
      const applicantDebts = JSON.parse(JSON.stringify(applicant.debts || []));
      const debtCopy = JSON.parse(JSON.stringify(debt));
      if (!applicantDebts.some((applicantDebt) => applicantDebt.id === debtCopy.id)) {
        applicantDebts.push(debtCopy);
      }
      const clientInfo = {
        id: applicant.id,
        firstName: applicant.first_name,
        lastName: applicant.last_name,
        type: "Žiadateľ",
        type_code: "applicant",
      };

      // Detail dlhu používa rovnaký editor ako karta hypotéky. Pripravíme mu
      // preto kontext aktuálneho servisovaného klienta bez ďalšej navigácie.
      this.$store.commit("mortgageDetailState/setMortgage", mortgage);
      this.$store.commit("mortgageDetailState/setClientDebts", {
        debts: applicantDebts,
        client: "applicant",
      });
      this.$store.commit("mortgageDetailState/setApplicant", applicantCopy);
      this.$store.commit("popupManagerState/setClientInfo", clientInfo);
      this.$store.commit("popupManagerState/pushToPopupOrder", {
        type: {debtType: debt.typ, debtPurpose: "classic"},
        payload: debtCopy,
        component: "ClientDebtPopUp",
        rules: ["priority"],
      });
    },
    async refinanceMortgage() {
      const servicedApplication = this.servicedApplication;

      if (!servicedApplication) return;

      if (!servicedApplication.vytvoreny_debt) {
        await this.$store.dispatch("mortgageDetailState/initializeClientDebtDti", {debts: this.mortgage.applicant.debts, role: "applicant"});
        this.$store.commit("popupManagerState/pushToPopupOrder", {
          type: "pickerOnly",
          payload: null,
          component: "ClientDebtPicker",
          rules: this.rules,
        });
      } else {
        this.refinanceMortgageForDebt(servicedApplication.vytvoreny_debt.id);
      }
    },
    async refinanceMortgageForDebt(debtId) {
      if (this.mortgage.applicant.debts.map(debt => debt.id).indexOf(debtId) === -1) {
        return;
      }

      const mortgageInfo = {
        applicant: this.mortgage.applicant.id,
        coapplicant: this.mortgage.coapplicant ? this.mortgage.coapplicant.id : null,
        stage: "vyber_riesenia",
        povodna_servisna_hypoteka: this.mortgage.id,
        // ciel_prva_analyza: "refinancovanie" TODO: Preveriť či je vhodné
      }

      try {
        const refinancedMortgage = await this.$store.dispatch("mortgagesState/postMortgage", mortgageInfo);

        this.$store.commit("appState/setLoadingPage", true);
        await patchMortgageDebtPlan(refinancedMortgage.id, debtId, "refinance");

        this.$router.push({
          name: "KartaHypotekyDetail",
          params: {hypoteka: refinancedMortgage.id},
        });
      } catch {
        this.$store.commit("appState/setSuccessMessage", {
          text: `Nepodarilo sa vytvoriť refinancovanie pre čerpanú hypotéku`,
          type: "error"
          }
        );
      } finally {
        this.$store.commit("appState/setLoadingPage", false);
      }
    },
    calculateFixEndDate() {
      const debt = this.servicedApplication.vytvoreny_debt;
      const fix = debt?.fixacia || this.servicedApplication.fix;
      const fixationStart = this.mortgage.zaciatok_fixacie || debt?.zaciatok || this.mortgage.nacerpana;
      const drainedDate = new Date(`${fixationStart}T00:00:00`);
      drainedDate.setFullYear(drainedDate.getFullYear() + fix);
      return this.formatShortSlovakDate(drainedDate);
    },
    formatShortSlovakDate(date) {
      const month = this.slovakMonthAbbr[date.getMonth()];
      const year = date.getFullYear();
      return `${month} ${year}`;
    },
    totalAmounts(application) {
      return application.suma_bezucel + application.suma_refinancovanie + application.suma_nadobudnutie;
    },
  },
}
</script>

<style scoped>
.serviced-mortgage-group {margin-bottom:22px;}
.serviced-mortgage-group .mortgage-result-servised {margin-bottom:0; border-radius:14px 14px 0 0;}
.serviced-mortgage-clickable {
  cursor: pointer;
  outline: none;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.serviced-mortgage-clickable:hover,
.serviced-mortgage-clickable:focus-visible {
  transform: translateY(-1px);
  border-color: color-mix(in srgb, var(--app-primary) 42%, var(--app-border));
  box-shadow: 0 12px 30px rgba(22, 78, 145, 0.12);
}

.serviced-mortgage-clickable--disabled {
  cursor: default;
}

.serviced-mortgage-clickable--disabled:hover,
.serviced-mortgage-clickable--disabled:focus-visible {
  transform: none;
  box-shadow: none;
}
</style>
